import * as React from "react";
import { Box, Card, CardHeader, List, Stack, Typography } from "@mui/material";
import CclDrawer from "../../components/common/cclDrawer/cclDrawer";
import CclDrawerActionBar from "../../components/common/cclDrawer/cclDrawerActionBar";
import CclButton from "../../components/common/cclButtons/cclButton";
import CclLoadingButton from "../../components/common/cclButtons/cclLoadingButton";
import StyledDropzone from "./styledDropzone";
import UploadFileListItem from "./uploadFileListItem";
import useLogAccessEvent from "../../hooks/useLogAccessEvent";
import { AccessEventDocumentAddedToSessionDetails } from "../../services/types/accessEventTypes";
import { DocumentItem } from "../../services/types/assessmentAPITypes";

type FileUploadItem = {
    file: File;
    uploadAttempted: boolean;
};

export interface DocumentUploadDrawerProps {
    open: boolean;
    onClose: () => void;
    onUploadComplete: (document: DocumentItem) => void;
}

const DocumentUploadDrawer: React.FC<DocumentUploadDrawerProps> = (props) => {
    const [uploadInProgress, setUploadInProgress] = React.useState<boolean>(false);
    const [fileUploadItem, setFileUploadItem] = React.useState<FileUploadItem | null>(null);
    const { logEvent } = useLogAccessEvent();

    const canUpload = fileUploadItem !== null && !fileUploadItem.uploadAttempted;

    const addFile = (acceptedFiles: File[]) => {
        if (acceptedFiles.length === 0 || fileUploadItem !== null) return;
        setFileUploadItem({
            file: acceptedFiles[0],
            uploadAttempted: false,
        });
    };

    const removeFile = () => {
        setFileUploadItem(null);
        setUploadInProgress(false);
    };

    const fileUploadResult = (success: boolean, document?: DocumentItem) => {
        setUploadInProgress(false);
        if (success && document) {
            const eventDetails: AccessEventDocumentAddedToSessionDetails = {
                documentId: document.documentId.toString(),
                documentTitle: document.documentName,
            };
            logEvent("DocumentUploaded", eventDetails);
            props.onUploadComplete(document);
        }
    };

    const startUpload = () => {
        if (!canUpload) return;
        setUploadInProgress(true);
        setFileUploadItem((prev) => (prev ? { ...prev, uploadAttempted: true } : null));
    };

    const handleClose = () => {
        setFileUploadItem(null);
        setUploadInProgress(false);
        props.onClose();
    };

    return (
        <CclDrawer title="Upload Document" open={props.open} onClose={handleClose}>
            <Box width={1} sx={{ p: 2 }}>
                <Stack direction="column" spacing={2} width={1}>
                    <Card sx={{ width: 1 }}>
                        <CardHeader
                            title={"Select Document For Upload"}
                            sx={{ mt: 1, pt: 2, mb: 0, pb: 2 }}
                        />
                        <Stack spacing={3} sx={{ pl: 3, pr: 3, pt: 1 }}>
                            <StyledDropzone
                                disabled={fileUploadItem !== null}
                                onAddFile={addFile}
                            />
                            <Stack
                                width={1}
                                direction="row"
                                display="flex"
                                justifyContent="space-between"
                                sx={{ pl: 2, pr: 2 }}
                            >
                                <CclLoadingButton
                                    size="small"
                                    onClick={startUpload}
                                    disabled={!canUpload}
                                    mode={"primary"}
                                    loading={uploadInProgress}
                                >
                                    Upload Document
                                </CclLoadingButton>
                                <CclButton
                                    disabled={fileUploadItem === null || uploadInProgress}
                                    size="small"
                                    onClick={removeFile}
                                    mode={"secondary"}
                                >
                                    Clear
                                </CclButton>
                            </Stack>
                            <Typography variant="subtitle1" sx={{ pl: 2, pr: 2 }}>
                                Document To Upload
                            </Typography>
                            <List sx={{ mt: 0, pb: 0, borderBottom: "1px solid lightgray" }}>
                                {fileUploadItem && (
                                    <UploadFileListItem
                                        key={fileUploadItem.file.name}
                                        file={fileUploadItem.file}
                                        startUpload={fileUploadItem.uploadAttempted}
                                        onUploadResult={fileUploadResult}
                                    />
                                )}
                            </List>
                        </Stack>
                    </Card>
                </Stack>
            </Box>
            <CclDrawerActionBar>
                <CclButton id="close-btn" onClick={handleClose} mode={"primary"}>
                    Close
                </CclButton>
            </CclDrawerActionBar>
        </CclDrawer>
    );
};

export default DocumentUploadDrawer;
